import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";

export default function ResetPassword() {
  const { t, i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (pwd !== confirm) { setError(t("register.errMismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err: any) {
      const d = err?.response?.data?.detail;
      setError(d === "INVALID_OR_EXPIRED_TOKEN" ? t("reset.invalidToken") : d === "PASSWORD_TOO_SHORT" ? t("register.errShort") : apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] flex items-center justify-center px-4">
      <div className="w-full max-w-md fade-up">
        <div className="swiss-card p-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#C8A44D]">Vytali Academy</p>
          <h1 className="font-heading text-2xl font-semibold mt-2">{t("reset.title")}</h1>
          {done ? (
            <div className="mt-5 flex items-start gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-3" data-testid="reset-success">
              <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
              <p className="text-sm">{t("reset.success")}</p>
            </div>
          ) : !token ? (
            <p className="mt-5 text-sm text-[#FF3B30]" data-testid="reset-no-token">{t("reset.invalidToken")}</p>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="reset-form">
              <div>
                <label className="block text-sm font-medium mb-1.5">{fr ? "Nouveau mot de passe" : "New password"}</label>
                <input data-testid="reset-password-input" type="password" required minLength={8} value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("register.confirm")}</label>
                <input data-testid="reset-confirm-input" type="password" required minLength={8} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-[#FF3B30] font-medium">{error}</p>}
              <button data-testid="reset-submit" type="submit" disabled={loading}
                className="w-full py-2.5 bg-[#002BF6] text-white font-medium rounded-md hover:bg-[#0024cc] transition-colors duration-200 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                <KeyRound size={15} /> {loading ? "…" : t("reset.submit")}
              </button>
            </form>
          )}
          <Link to="/login" data-testid="reset-back-login" className="mt-4 block text-center text-sm text-[#002BF6] hover:underline">{t("login.backToLogin")}</Link>
        </div>
      </div>
    </div>
  );
}
